/**
 * User Context Utility
 * Provides centralized access to current user's employeeId from localStorage
 * This allows service layer to automatically inject whoAdded/whoUpdated without requiring components to pass employeeId
 */

/**
 * Gets the current employee ID from localStorage
 * Tries multiple fields in order: employeeId, id, _id
 * 
 * @returns The employee ID string or null if not available
 */
export const getCurrentEmployeeIdFromStorage = (): string | null => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) {
      console.warn('[USER-CONTEXT] No userData found in localStorage');
      return null;
    }
    
    const user = JSON.parse(userData);
    // Try employeeId first, then id, then _id as fallback
    const employeeId = user.employeeId ;
    
    if (!employeeId) {
      console.warn('[USER-CONTEXT] No employeeId found in userData:', user);
      return null;
    }
    
    return String(employeeId);
  } catch (error) {
    console.error('[USER-CONTEXT] Error parsing userData from localStorage:', error);
    return null;
  }
};

/**
 * Gets the current employee ID from localStorage (throws error if not available)
 * Use this in service methods that require authentication
 * 
 * @returns The employee ID string
 * @throws Error if user is not authenticated or employeeId is not available
 */
export const requireEmployeeId = (): string => {
  const employeeId = getCurrentEmployeeIdFromStorage();
  
  if (!employeeId) {
    const error = new Error('User is not authenticated. Please log in to continue.');
    console.error('[USER-CONTEXT]', error.message);
    throw error;
  }
  
  return employeeId;
};

/**
 * Gets the current employee ID from localStorage (safe version)
 * Returns undefined instead of throwing if not available
 * 
 * @returns The employee ID string or undefined if not available
 */
export const getCurrentEmployeeIdSafe = (): string | undefined => {
  const employeeId = getCurrentEmployeeIdFromStorage();
  return employeeId || undefined;
};

/** True when the logged-in user has the admin or super_admin role (JWT / userData). */
export const getCurrentUserIsAdmin = (): boolean => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return false;
    const user = JSON.parse(userData) as { role?: string; roles?: string[] };
    const roles = user.roles || [];
    return (
      user.role === 'admin' ||
      user.role === 'super_admin' ||
      roles.includes('admin') ||
      roles.includes('super_admin')
    );
  } catch {
    return false;
  }
};

/** True when the logged-in user has the super_admin role. */
export const getCurrentUserIsSuperAdmin = (): boolean => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return false;
    const user = JSON.parse(userData) as { role?: string; roles?: string[] };
    const roles = user.roles || [];
    return user.role === 'super_admin' || roles.includes('super_admin');
  } catch {
    return false;
  }
};
